var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-wiki-view.ts
import { subscribeByID } from "@adornis/baseql/operations/mongo.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { acss } from "@adornis/chemistry/directives/acss.js";
import { css } from "@adornis/chemistry/directives/css.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { routingState } from "@adornis/router/client/routing-state.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { switchMap, tap } from "rxjs";
import { WikiEntry } from "../db/WikiEntry.js";
import { WikiEntryDraft, WikiEntryDraftStatus } from "../db/WikiEntryDraft.js";
import { queryAll } from "./query-shadow-root.js";
import "./x-wiki-user-text.js";
function isChildOfAccordionOrHeader(node) {
  let cur = node.parentElement;
  while (cur) {
    if (cur.tagName === "DIV" && cur.getAttribute("slot") === "accordeon-title") {
      return true;
    }
    if (cur.tagName.endsWith("H1") || cur.tagName.endsWith("H2") || cur.tagName.endsWith("H3") || cur.tagName.endsWith("H4") || cur.tagName.endsWith("H5") || cur.tagName.endsWith("H6")) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}
var XWikiView = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.t = new TranslationController(this);
    this.entryID = new RXController(this, "");
    this._entry = new RXController(
      this,
      this.entryID.observable.pipe(
        switchMap(
          (id) => subscribeByID(
            WikiEntry,
            id ?? ""
          )({
            breadcrumbs: {
              title: 1,
              entryID: 1
            },
            children: {
              _id: 1,
              title: 1
            },
            parentID: 1,
            title: 1,
            content: 1,
            createdAt: 1,
            modifiedAt: 1,
            published: 1,
            referenceID: 1,
            status: 1,
            createdByID: 1,
            modifiedByID: 1
          })
        )
      )
    );
    this._jumpTo = new RXController(
      this,
      routingState.searchParams.get("jumpTo").pipe(
        tap((updated) => {
          this.requestUpdate();
        })
      )
    );
    this._highlightTags = new RXController(
      this,
      routingState.searchParams.get("highlightTags").pipe(
        tap((updated) => {
          this.requestUpdate();
        })
      )
    );
    /**
     * Reference to the content container element for scroll functionality
     */
    this._contentRef = createRef();
  }
  /**
   * Lifecycle method called after the component updates.
   * Handles the jump-to functionality by scrolling to matching content.
   * @param prop - Changed properties
   */
  updated(prop) {
    super.updated(prop);
    if (this._jumpTo.value && this._contentRef.value) {
      queryAll(".wiki-search-selected").forEach((el) => {
        el.classList.remove("wiki-search-selected");
      });
      let node = queryAll("*", this._contentRef.value).find((el) => {
        if (!this._jumpTo.value)
          return false;
        if (!isChildOfAccordionOrHeader(el))
          return false;
        if (el instanceof HTMLElement) {
          return el.innerText.trim() === this._jumpTo.value.trim();
        }
        return false;
      });
      if (node) {
        if (node.tagName === "NODE-ACCORDEON-CONTAINER") {
          node = node.children[0];
        }
        let parent = node.parentElement;
        let toOpen = [];
        while (parent) {
          if (parent.nodeName === "NODE-ACCORDEON") {
            toOpen.push(parent);
          }
          parent = parent.parentElement;
        }
        toOpen.toReversed().forEach((el, i) => {
          setTimeout(() => {
            el.open();
          }, i * 150);
        });
        if (toOpen.length) {
          setTimeout(() => {
            if (!node) {
              return;
            }
            node.scrollIntoView({ behavior: "smooth" });
            node.classList.add("wiki-search-selected");
          }, toOpen.length * 150);
        } else {
          node.scrollIntoView({ behavior: "smooth" });
          node.classList.add("wiki-search-selected");
        }
      }
    }
    if (this._highlightTags.value && this._contentRef.value) {
      queryAll("node-tag", this._contentRef.value).forEach((el) => {
        if (el.textContent?.toLowerCase().includes(this._highlightTags.value?.toLowerCase() ?? "")) {
          el.classList.add("wiki-search-selected");
        }
        let parent = el.parentElement;
        let toOpen = [];
        while (parent) {
          if (parent.nodeName === "NODE-ACCORDEON") {
            toOpen.push(parent);
          }
          parent = parent.parentElement;
        }
        toOpen.toReversed().forEach((el2, i) => {
          setTimeout(() => {
            el2.open();
          }, i * 150);
        });
      });
    }
  }
  /**
   * Checks if the current entry is a draft
   * @returns {boolean} True if the entry is a WikiEntryDraft
   */
  _isDraft() {
    return this._entry.value instanceof WikiEntryDraft;
  }
  /**
   * Renders the header section of the wiki entry
   * @returns {Renderable} Header template with title, publish status, and draft indicators
   */
  _header() {
    let content = html`<x-loader ${css({ width: "18px", height: "18px", margin: `${this.spacing.sm} 0` })}></x-loader>`;
    if (!this._entry.isLoading) {
      content = html`
        ${!this._entry.value?.published ? html`
              <x-icon ${css({ opacity: 0.8 })}>
                visibility_off
                <x-tooltip><x-text>${this.t.translate("wiki", "notPublishedTooltip")}</x-text></x-tooltip>
              </x-icon>
            ` : nothing}
        <x-h1> ${this._entry.value?.title ?? this.t.translate("wiki", "notFound")} </x-h1>
      `;
    }
    return html`
      <x-flex
        slot="title"
        space-between
        center
        horizontal
        ${css({
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      paddingBottom: this.spacing.sm,
      marginBottom: this.spacing.sm
    })}
      >
        <x-flex space="sm" horizontal crossaxis-center> ${content} </x-flex>
        <x-flex horizontal space="sm" ${css({ opacity: this._entry.isLoading ? 0 : 1, transition: "opacity 0.2s" })}>
          <slot name="title-right"> </slot>
          ${this._isDraft() ? html`
                <x-flex
                  horizontal
                  space="sm"
                  crossaxis-center
                  ${css({
      backgroundColor: "rgba(0,0,0,0.1)",
      borderRadius: this.sizes.borderRadius,
      padding: `${this.spacing.xxs} ${this.spacing.sm}`
    })}
                >
                  <x-text
                    >${this.t.translate("wiki", "draft")}:
                    ${WikiEntryDraftStatus[this._entry.value?.status]}</x-text
                  >
                  <x-icon>edit_note</x-icon>
                </x-flex>
              ` : nothing}
        </x-flex>
      </x-flex>
    `;
  }
  /**
   * Renders the subtitle section of the wiki entry
   * @returns {Renderable} Subtitle template with breadcrumbs and creation/modification times
   */
  _subtitle() {
    if (this._entry.isLoading)
      return nothing;
    return html`
      <x-flex
        ${acss({
      ".link:hover": {
        opacity: "0.5",
        cursor: "pointer"
      }
    })}
        slot="subtitle"
        horizontal
        ?space-between=${this._entry.value?.breadcrumbs?.length || this._isDraft()}
        crossaxis-center
        ${css({ marginBottom: this.spacing.lg })}
      >
        ${!this._isDraft() ? html`
              <x-flex horizontal space="sm" slot="breadcrumbs">
                ${repeat(
      this._entry.value?.breadcrumbs ?? [],
      ({ entryID }) => entryID,
      ({ entryID, title }, i) => {
        return html`
                      <x-text class="link">
                        <x-link
                          @click=${() => {
          this.dispatchEvent(
            new CustomEvent("entry-navigate", {
              detail: {
                value: entryID
              }
            })
          );
        }}
                        >
                          ${title}
                        </x-link>
                      </x-text>
                      ${i < (this._entry.value?.breadcrumbs?.length ?? 0) - 1 ? html`<x-text>•</x-text>` : nothing}
                    `;
      }
    )}
              </x-flex>
            ` : html`
              <x-flex class="link">
                <x-link
                  @click=${() => {
      this.dispatchEvent(
        new CustomEvent("entry-navigate", {
          detail: {
            value: this._entry.value.referenceID
          }
        })
      );
    }}
                >
                  <x-text>${this.t.translate("wiki", "toOriginal")}</x-text>
                </x-link>
              </x-flex>
            `}
        <x-text slot="times" ${css({ opacity: 0.5 })}>
          ${(() => {
      const templates = [
        this._entry.value?.createdAt ? html`${this.t.translate("wiki", "created")}:
                  ${this._entry.value.createdAt.toFormat("dd.MM.yyyy HH:mm")}` : this.t.translate("wiki", "createdMissing"),
        this._entry.value?.modifiedAt ? html`${this.t.translate("wiki", "modified")}:
                    ${this._entry.value.modifiedAt.toFormat("dd.MM.yyyy HH:mm")}
                    <x-wiki-user-text .userID=${this._entry.value.modifiedByID}> </x-wiki-user-text>` : null
      ].filter(Boolean);
      for (let i = 0; i < templates.length - 1; i++) {
        templates[i] = html`${templates[i]} • `;
      }
      return templates;
    })()}
        </x-text>
      </x-flex>
    `;
  }
  /**
   * Renders the children section of the wiki entry
   * @returns {Renderable} Children template with subpage links
   */
  _children() {
    if (!this._entry.value || !this._entry.value.children || this._entry.value.children.length === 0)
      return nothing;
    return html`
      <x-flex slot="children" ${css({ marginBottom: this.spacing.lg })}>
        <x-flex
          space-between
          center
          horizontal
          ${css({
      marginTop: this.spacing.md,
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      paddingBottom: this.spacing.sm,
      marginBottom: this.spacing.sm
    })}
        >
          <x-h4>${this.t.translate("wiki", "subpages")}</x-h4>
        </x-flex>

        <x-flex space="sm">
          ${repeat(
      this._entry.value.children,
      ({ _id, title }) => _id,
      ({ _id, title }, i) => {
        return html`
                <x-flex
                  ${acss({
          "&:hover": {
            opacity: "0.5",
            cursor: "pointer"
          }
        })}
                  horizontal
                  crossaxis-center
                  space="sm"
                  @click=${() => {
          this.dispatchEvent(
            new CustomEvent("entry-navigate", {
              detail: {
                value: _id
              }
            })
          );
        }}
                >
                  <x-icon>draft</x-icon>
                  <x-text>${title}</x-text>
                </x-flex>
              `;
      }
    )}
        </x-flex>
      </x-flex>
    `;
  }
  /**
   * Renders the main content of the wiki entry
   * @returns {Renderable} Content template with entry data or loading indicator
   */
  render() {
    let content = "";
    if (this._entry.isLoading || this._entry.value) {
      content = html`
        <x-flex padding="lg" crossaxis-center ${css({ display: !this._entry.isLoading ? "none" : "flex" })}>
          <x-loader></x-loader>
        </x-flex>
        <x-flex
          ${css({ opacity: this._entry.isLoading ? 0 : 1, transition: "opacity 0.75s" })}
          ${ref(this._contentRef)}
          ${acss({
        [`${DesignSystem.prefix}-text:empty, x-text:empty`]: {
          height: "19px"
        }
      })}
        >
          <style>
            .wiki-search-selected {
              background-color: rgba(255, 0, 0, 0.1);
              border: 2px solid rgba(255, 0, 0, 0.5);
              padding: 5px 10px;
              border-radius: 5px;
              transition: all 0.5s ease-in-out;
            }
          </style>
          ${xComponents(html`${unsafeHTML(this._entry.value?.content)}`)}
        </x-flex>
      `;
    } else {
      content = html` <x-text>${this.t.translate("wiki", "notFound")}</x-text> `;
    }
    return xComponents(html`
      <x-flex ${css({ maxWidth: "1400px", margin: "0 auto", padding: `${this.spacing.lg} 0` })}>
        ${this._header()} ${this._subtitle()} ${this._children()} ${content}
      </x-flex>
    `);
  }
};
__decorateClass([
  state()
], XWikiView.prototype, "t", 2);
__decorateClass([
  property()
], XWikiView.prototype, "entryID", 2);
__decorateClass([
  state()
], XWikiView.prototype, "_entry", 2);
__decorateClass([
  state()
], XWikiView.prototype, "_jumpTo", 2);
__decorateClass([
  state()
], XWikiView.prototype, "_highlightTags", 2);
XWikiView = __decorateClass([
  customElement("x-wiki-view")
], XWikiView);
export {
  XWikiView
};
